import { useEffect, useState } from "react";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, downloadFile, shortDate } from "@/lib/api";
import { GoldProgress, StatusPill } from "@/components/StatusPill";
import { TimelinePanel } from "@/components/panels/TimelinePanel";
import { DocumentsPanel } from "@/components/panels/DocumentsPanel";
import { MessagesPanel } from "@/components/panels/MessagesPanel";
import { TasksPanel } from "@/components/panels/TasksPanel";
import { InvoicesPanel } from "@/components/panels/InvoicesPanel";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TABS = ["Details", "Timeline", "Documents", "Messages", "Tasks", "Invoices"];
const STATUSES = ["open", "in_progress", "review", "closed"];

export function CaseDrawer({ caseId, onClose, onChanged, investigators = [], role }) {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!caseId) {
      setDetail(null);
      return;
    }
    api.get(`/cases/${caseId}`).then(({ data }) => setDetail(data));
  }, [caseId]);

  const patch = async (updates) => {
    try {
      const { data } = await api.patch(`/cases/${caseId}`, updates);
      setDetail((prev) => ({ ...prev, case: data }));
      toast.success("Case updated");
      onChanged?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const c = detail?.case;

  return (
    <Sheet open={!!caseId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        data-testid="case-drawer"
        className="glass-strong border-l border-white/10 w-full sm:max-w-2xl overflow-y-auto scroll-thin text-[#F3F4F6]"
      >
        {!c ? <SheetTitle className="sr-only">Case detail</SheetTitle> : null}
        {c ? (
          <>
            <SheetHeader className="text-left">
              <p className="text-xs uppercase tracking-[0.25em] text-gold">{c.reference}</p>
              <SheetTitle className="font-display text-2xl text-[#F3F4F6]" data-testid="drawer-case-title">
                {c.title}
              </SheetTitle>
              <p className="text-sm text-[#9CA3AF]">
                {c.client_name} · {c.client_email} · opened {shortDate(c.created_at)}
              </p>
            </SheetHeader>

            <Tabs defaultValue="Details" className="mt-7">
              <TabsList className="bg-white/[0.04] border border-white/10 rounded-full p-1 flex-wrap h-auto">
                {TABS.map((t) => (
                  <TabsTrigger
                    key={t}
                    value={t}
                    data-testid={`drawer-tab-${t.toLowerCase()}`}
                    className="rounded-full px-3.5 py-1.5 text-xs data-[state=active]:bg-gold data-[state=active]:text-navy text-[#9CA3AF]"
                  >
                    {t}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="Details" className="mt-7 space-y-7">
                <div className="glass rounded-xl p-5 space-y-5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-widest text-[#9CA3AF]">Status</span>
                    <select
                      data-testid="drawer-status-select"
                      value={c.status}
                      onChange={(e) => patch({ status: e.target.value })}
                      className="rounded-full bg-white/5 border border-white/15 px-3 py-1.5 text-xs"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s.replace(/_/g, " ")}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-widest text-[#9CA3AF]">Investigator</span>
                    <select
                      data-testid="drawer-investigator-select"
                      value={c.investigator_id || ""}
                      onChange={(e) => patch({ investigator_id: e.target.value })}
                      className="rounded-full bg-white/5 border border-white/15 px-3 py-1.5 text-xs max-w-[220px]"
                    >
                      <option value="">Unassigned</option>
                      {investigators.map((u) => (
                        <option key={u.id} value={u.id}>{`${u.name} (${u.role})`}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-widest text-[#9CA3AF]">Priority</span>
                    <select
                      data-testid="drawer-priority-select"
                      value={c.priority}
                      onChange={(e) => patch({ priority: e.target.value })}
                      className="rounded-full bg-white/5 border border-white/15 px-3 py-1.5 text-xs"
                    >
                      {["low", "medium", "high"].map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-widest text-[#9CA3AF]">Progress</span>
                      <span className="font-display text-lg text-gold">{c.progress}%</span>
                    </div>
                    <input
                      data-testid="drawer-progress-slider"
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      defaultValue={c.progress}
                      onMouseUp={(e) => patch({ progress: Number(e.target.value) })}
                      onTouchEnd={(e) => patch({ progress: Number(e.target.value) })}
                      className="mt-3 w-full accent-[#D4AF37]"
                    />
                    <GoldProgress value={c.progress} className="mt-2" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {[
                    ["Documents", detail.counts.documents],
                    ["Open tasks", detail.counts.open_tasks],
                    ["Messages", detail.counts.messages],
                    ["Invoices", detail.counts.invoices],
                  ].map(([k, v]) => (
                    <div key={k} className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                      <p className="font-display text-xl">{v}</p>
                      <p className="text-[11px] uppercase tracking-widest text-[#6B7280]">{k}</p>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <StatusPill value={c.status} />
                  <span className="text-xs text-[#6B7280]">Category: {c.category}</span>
                  <Button
                    data-testid="drawer-summary-pdf-btn"
                    size="sm"
                    variant="outline"
                    className="ml-auto rounded-full border-white/20 bg-transparent hover:bg-white/10"
                    onClick={() =>
                      downloadFile(`/cases/${c.id}/summary`, `${c.reference}-summary.pdf`, { format: "pdf" }).catch(() =>
                        toast.error("Export failed"),
                      )
                    }
                  >
                    <FileText className="h-4 w-4 mr-2 text-gold" /> Case summary PDF
                  </Button>
                </div>

                <p className="text-sm leading-relaxed text-[#D1D5DB]">{c.description}</p>
              </TabsContent>

              <TabsContent value="Timeline" className="mt-7">
                <TimelinePanel caseId={c.id} canEdit />
              </TabsContent>
              <TabsContent value="Documents" className="mt-7">
                <DocumentsPanel caseId={c.id} uploadLabel="Case documents" />
              </TabsContent>
              <TabsContent value="Messages" className="mt-7">
                <MessagesPanel caseId={c.id} currentRole={role} />
              </TabsContent>
              <TabsContent value="Tasks" className="mt-7">
                <TasksPanel caseId={c.id} isStaff />
              </TabsContent>
              <TabsContent value="Invoices" className="mt-7">
                <InvoicesPanel caseId={c.id} isStaff />
              </TabsContent>
            </Tabs>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
